import * as React from 'react';
import { Link } from 'react-router-dom';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import Tag from 'src/components/Tag';
import Title from 'src/components/Title';
import {
  ICourseSearchResultItem,
  INewsSearchResultItem
} from 'src/types/SearchResult';
import { getActivityTag } from 'src/utils/activityUtils';
import { formatDate } from 'src/utils/dateUtils';
import { getNewsItemTag } from 'src/utils/newsUtils';

const HIGHLIGHT_TEMPLATE = '<span class="search-highlight">$1</span>';

const keywordsToPattern = (
  keywords: ICourseSearchResultItem['listOfKeywords']
) => {
  const list = keywords.map(keyword => keyword.keyword).join('|');
  return new RegExp(`(${list})`, 'gi');
};

const extractDescription = (pattern: RegExp, description: string): string => {
  const matches = pattern.exec(description);
  if (!matches) {
    return description;
  }

  const start = Math.max(pattern.lastIndex - 60, 0);
  const end = Math.min(pattern.lastIndex + 60, description.length);
  let sliced = description.slice(start, end);

  if (start > 0) {
    sliced = `…${sliced}`;
  }

  if (end < description.length) {
    sliced = `${sliced}…`;
  }

  return sliced.replace(pattern, HIGHLIGHT_TEMPLATE);
};

interface ICourseSearchResultProps {
  results: ICourseSearchResultItem[];
}

interface IResultCategoryGroup {
  name: string;
  items: ICourseSearchResultItem[];
}

interface IResultGroup {
  name: string;
  items: IResultCategoryGroup[];
  courses: ICourseSearchResultItem[];
  categories: {
    [key: string]: IResultCategoryGroup;
  };
}

interface ICourseSearchResultItemProps {
  item: ICourseSearchResultItem;
}

const getCourseDate = (item: ICourseSearchResultItem) => {
  const { startDate, endDate, startTime, endTime } = item;

  if (startDate && endDate) {
    return `${formatDate(new Date(startDate), 'dd/MM/yyyy')} ${startTime} - ${formatDate(new Date(endDate), 'dd/MM/yyyy')} ${endTime}`;
  }

  return '';
};

const CourseSearchResultItem = ({ item }: ICourseSearchResultItemProps) => {
  const keywordsPattern = React.useMemo(
    () => keywordsToPattern(item.listOfKeywords),
    [item.listOfKeywords]
  );

  const tagProps = React.useMemo(() => getActivityTag(item), [item]);

  const title = React.useMemo(() => {
    return item.courseTitle.replace(
      keywordsPattern,
      HIGHLIGHT_TEMPLATE
    );
  }, [keywordsPattern, item.courseTitle]);

  const description = React.useMemo(() => {
    return item.courseDescription
      ? extractDescription(keywordsPattern, item.courseDescription)
      : '';
  }, [keywordsPattern, item.courseDescription]);

  return (
    <Link
      className="flex has-background-gray-500 mb-1 no-underline has-text-black"
      to={`/kursusside/${item.themeId}-${item.categoryId}-${item.courseId}`}
    >
      <div className="p-4 w-full">
        <div className="flex flex-wrap mb-1">
          {item.categoryName
            ? <Tag className="mr-1 ">{item.categoryName}</Tag>
            : null}
          {tagProps && (
            <Tag {...tagProps} className="mr-2" />
          )}
          {item.startDate
            ? <div className="mr-2 f5 font-medium">{getCourseDate(item)}</div>
            : null}
        </div>
        <div
          className="uppercase font-light leading-6"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <div className="f4 has-text-gray-800" dangerouslySetInnerHTML={{ __html: description }} />
      </div>
      <div className="flex items-center justify-center p-6" style={{ backgroundColor: '#D9D9DA' }}>
        <div className="w-6 h-6 has-background-gray-400 rounded-full">
          <Icon name="chevron-right" className="w-4 h-4 m-1" />
        </div>
      </div>
    </Link>
  );
};

export const CourseSearchResult = (props: ICourseSearchResultProps) => {
  const grouped = React.useMemo(() => {
    const list: IResultGroup[] = [];
    const groups: Record<string, IResultGroup> = {};

    props.results.forEach(item => {
      let group = groups[item.themeName];

      if (!group) {
        group = {
          name: item.themeName,
          items: [],
          courses: [],
          categories: {}
        };
        groups[item.themeName] = group;
        list.push(group);
      }

      let category = group.categories[item.categoryName];

      if (!category) {
        category = {
          name: item.categoryName,
          items: []
        };

        group.items.push(category);
        group.categories[category.name] = category;
      }

      group.courses.push(item);
      category.items.push(item);
    });

    return list;
  }, [props.results]);

  return (
    <div className="-mb-10">
      {grouped.map(group => (
        <div key={group.name} className="mb-10">
          {group.items.map(category => (
            <div className="mb-2" key={category.name}>
              <div className="pl-5 px-4 py-3 font-bold uppercase has-text-black f5">
                {`Kurser i: ${category.name} (${category.items.length} stk.)`}
              </div>
              {category.items.map(item => (
                <CourseSearchResultItem key={item.courseId} item={item} />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

interface INewsSearchResultItemProps {
  item: INewsSearchResultItem;
  onClick: (item: INewsSearchResultItem) => void;
}

const NewsSearchResultItem = ({
  item,
  onClick
}: INewsSearchResultItemProps) => {
  const keywordsPattern = React.useMemo(
    () => keywordsToPattern(item.listOfKeywords),
    [item.listOfKeywords]
  );

  const title = React.useMemo(() => {
    return item.title.replace(
      keywordsPattern,
      HIGHLIGHT_TEMPLATE
    );
  }, [keywordsPattern, item.title]);

  const description = React.useMemo(() => {
    return item.content
      ? extractDescription(keywordsPattern, item.content)
      : '';
  }, [keywordsPattern, item.content]);

  const tagProps = getNewsItemTag(item.newsLabel);

  return (
    <div className="has-background-white p-4 mb-1 block no-underline has-text-black">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div
            className="font-bold"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <div dangerouslySetInnerHTML={{ __html: description }} />
        </div>
        <div className="mr2-md mb1 mb0-md">
          {tagProps && (
            <Tag {...tagProps} />
          )}
        </div>
        <div
          onClick={() => onClick(item)}
          className="f5 flex-none font-semibold w-28 flex items-center justify-between"
        >
          {item.newsDate
            ? formatDate(new Date(item.newsDate), 'dd.MM.yyyy')
            : ''}
          <Button rounded={true} color="gray" size="small" className="ml-4">
            <Icon name="chevron-right" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export interface INewsSearchResultProps {
  results: INewsSearchResultItem[];
  onClick: (item: INewsSearchResultItem) => void;
}

export const NewsSearchResult = (props: INewsSearchResultProps) => (
  <div>
    <Title className="mb-6">Søgeresultater - nyheder</Title>
    <div style={{ backgroundColor: '#D9D9DA' }} className="pl-4 pb-3">
      <div className="pt-2 px-4 flex items-center justify-between">
        <div className="f5 font-bold py-2">Titel</div>
        <div className="f5 font-bold py-2 flex-none w-28">Dato</div>
      </div>
      {props.results.map(item => (
        <NewsSearchResultItem
          key={item.newsId}
          item={item}
          onClick={props.onClick}
        />
      ))}
    </div>
  </div>
);



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/SeachPage/SearchResult.component.tsx